import { VehicleValuationErrorCode, VehicleValuationErrorProducer } from "@/api-schema-enums";

interface VehicleValuationFailureDisplayBaseConfig {
  titleKey: string;
  descriptionKey: string;
}

interface VehicleValuationFailureDisplayConfig extends VehicleValuationFailureDisplayBaseConfig {
  detailLabelKey: string;
}

interface VehicleValuationErrorCodeDisplayConfig extends VehicleValuationFailureDisplayBaseConfig {
  producer: VehicleValuationErrorProducer;
}

interface VehicleValuationFailureDisplayBase extends VehicleValuationFailureDisplayBaseConfig {
  producer: VehicleValuationErrorProducer;
}

interface VehicleValuationFailureDisplayWithDetail extends VehicleValuationFailureDisplayBase {
  detailLabelKey: string;
  detail: string;
  secondaryDetail: string | null;
}

interface VehicleValuationFailureDisplayWithoutDetail extends VehicleValuationFailureDisplayBase {
  detailLabelKey: null;
  detail: null;
  secondaryDetail: null;
}

export type VehicleValuationFailureDisplay =
  | VehicleValuationFailureDisplayWithDetail
  | VehicleValuationFailureDisplayWithoutDetail;

type VehicleValuationFailureDetail = Pick<VehicleValuationFailureDisplayWithDetail, "detail" | "secondaryDetail">;

const VEHICLE_VALUATION_FAILURE_DISPLAY_CONFIG = {
  [VehicleValuationErrorProducer.Dat]: {
    titleKey: "fab:VehicleValuationDatFailureTitle",
    descriptionKey: "fab:VehicleValuationDatFailureDescription",
    detailLabelKey: "fab:VehicleValuationDatErrorCode",
  },
  [VehicleValuationErrorProducer.Aws]: {
    titleKey: "fab:VehicleValuationAwsFailureTitle",
    descriptionKey: "fab:VehicleValuationAwsFailureDescription",
    detailLabelKey: "fab:VehicleValuationAwsErrorDetail",
  },
  [VehicleValuationErrorProducer.Alphartis]: {
    titleKey: "fab:VehicleValuationAlphartisFailureTitle",
    descriptionKey: "fab:VehicleValuationAlphartisFailureDescription",
    detailLabelKey: "fab:VehicleValuationAlphartisErrorDetail",
  },
} as const satisfies Record<VehicleValuationErrorProducer, VehicleValuationFailureDisplayConfig>;

const VEHICLE_VALUATION_ERROR_CODE_DISPLAY_CONFIG = {
  [VehicleValuationErrorCode.DatBrandMappingMissing]: {
    titleKey: "fab:VehicleValuationDatBrandMappingMissingFailureTitle",
    descriptionKey: "fab:VehicleValuationDatBrandMappingMissingFailureDescription",
    producer: VehicleValuationErrorProducer.Alphartis,
  },
  [VehicleValuationErrorCode.DatBrandMappingMissingNoMasterdataAdmin]: {
    titleKey: "fab:VehicleValuationDatBrandMappingMissingFailureTitle",
    descriptionKey: "fab:VehicleValuationDatBrandMappingMissingNoMasterdataAdminFailureDescription",
    producer: VehicleValuationErrorProducer.Alphartis,
  },
  [VehicleValuationErrorCode.DatBrandMappingMissingNotificationFailed]: {
    titleKey: "fab:VehicleValuationDatBrandMappingMissingFailureTitle",
    descriptionKey: "fab:VehicleValuationDatBrandMappingMissingNotificationFailedFailureDescription",
    producer: VehicleValuationErrorProducer.Alphartis,
  },
} as const satisfies Record<VehicleValuationErrorCode, VehicleValuationErrorCodeDisplayConfig>;

export type VehicleValuationFailureFallbacks = Record<VehicleValuationErrorProducer, string>;

function isVehicleValuationErrorProducer(value: unknown): value is VehicleValuationErrorProducer {
  return Object.values(VehicleValuationErrorProducer).includes(value as VehicleValuationErrorProducer);
}

function resolveErrorProducer(value: VehicleValuationErrorProducer | null | undefined): VehicleValuationErrorProducer {
  return isVehicleValuationErrorProducer(value) ? value : VehicleValuationErrorProducer.Dat;
}

function isVehicleValuationErrorCode(value: unknown): value is VehicleValuationErrorCode {
  return Object.values(VehicleValuationErrorCode).includes(value as VehicleValuationErrorCode);
}

function resolveErrorCode(value: VehicleValuationErrorCode | null | undefined): VehicleValuationErrorCode | null {
  return isVehicleValuationErrorCode(value) ? value : null;
}

function splitDatFailureDetail(detail: string): VehicleValuationFailureDetail {
  const separatorIndex = detail.indexOf(".: ");

  if (separatorIndex < 0) {
    return {
      detail,
      secondaryDetail: null,
    };
  }

  return {
    detail: detail.slice(0, separatorIndex + 1),
    secondaryDetail: detail.slice(separatorIndex + 3).trim() || null,
  };
}

export function resolveVehicleValuationFailureDisplay(
  errorProducer: VehicleValuationErrorProducer | null | undefined,
  errorCode: VehicleValuationErrorCode | null | undefined,
  errorMessage: string | null | undefined,
  fallbacks: VehicleValuationFailureFallbacks,
): VehicleValuationFailureDisplay {
  const producer = resolveErrorProducer(errorProducer);
  const resolvedErrorCode = resolveErrorCode(errorCode);

  if (resolvedErrorCode) {
    const codeConfig = VEHICLE_VALUATION_ERROR_CODE_DISPLAY_CONFIG[resolvedErrorCode];

    return {
      ...codeConfig,
      detail: null,
      detailLabelKey: null,
      secondaryDetail: null,
    };
  }

  const config = VEHICLE_VALUATION_FAILURE_DISPLAY_CONFIG[producer];
  const trimmedErrorMessage = errorMessage?.trim();
  const detail = trimmedErrorMessage || fallbacks[producer];
  const resolvedDetail =
    producer === VehicleValuationErrorProducer.Dat
      ? splitDatFailureDetail(detail)
      : {
          detail,
          secondaryDetail: null,
        };

  return {
    ...config,
    ...resolvedDetail,
    producer,
  };
}
